/*
 * File Description: This file contains tools used in the front end of the application
 * Usage :
 *      import Tools from "@/utils/tools";
 *      Tools.StringHelper.format(...)
 *
 * To declare here a new type of helper :
 *      declare a new HelperManager :
 *          var <type>HelperManager = function () {
 *              var _self = this;
 *              _self.<functionName> = function (...) {
 *              };
 *          };
 *      instanciate the HelperManager :
 *          Tools.<Type>Helper = new <type>HelperManager();
 */

import i18n from "@/plugins/vue-i18n.js";
import moment from "moment";
import { dateFormat, dateFormatEnum } from "@/filter/dateFormat";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect/index.js";
import "flatpickr/dist/plugins/monthSelect/style.css";
import { convertToFlatpickrFormat } from "@/plugins/flatpickr";

const Tools = {};
export default Tools;

/** Business date Helper */
var businessDateHelperManager = function () {
  var _self = this;

  /** Business date for today, depending if it is before or after the time configured as the start of a business day */
  _self.getTodaysBusinessDate = function () {
    var now = moment(); //.minutes(0).seconds(0).milliseconds(0);
    return _self.getBusinessDateFromRealDatetime(now);
  };
  _self.getBusinessDateFromRealDatetime = function (momentDatetime) {
    if (momentDatetime && momentDatetime.isValid()) {
      if (momentDatetime.hour() < process.env.VUE_APP_START_DAY) {
        momentDatetime.date(momentDatetime.date() - 1);
      }
      momentDatetime.hour(process.env.VUE_APP_START_DAY);
      return momentDatetime;
    } else {
      throw "[getBusinessDateFromRealDatetime] error invalid momentDatetime";
    }
  };
  /** Renvoie la date théoriquement réelle (heure potentiellement arrondie) à partir des données de date et heure business */
  _self.getRealDateFromBusinessDateAndTime = function (
    year,
    month,
    day,
    hour,
    minute
  ) {
    var momentDatetime = moment()
      .year(year)
      .month(month)
      .date(day)
      .hour(hour)
      .minute(minute);
    if (momentDatetime.hour() < process.env.VUE_APP_START_DAY) {
      momentDatetime.date(momentDatetime.date() + 1);
    }
    return momentDatetime;
  };
  _self.getTodaysEndOfBusinessDate = function () {
    var now = moment().minutes(0).seconds(0).milliseconds(0);
    if (now.hour() >= process.env.VUE_APP_START_DAY) {
      now.date(now.date() + 1);
    }
    now.hours(process.env.VUE_APP_START_DAY);
    return now;
  };

  _self.getLabelPeriod = function (startDate, endDate, labelPrefix, startTime, endTime, useTimeFilter = true, isSiteOpeningHours = true) {
    const prefix = labelPrefix || "";

    if (startDate.isSame(endDate, 'day')) {
      return _self.getLabelPeriodOneDay(startDate, labelPrefix, useTimeFilter, startTime, endTime);
    }

    const siteOpeningHours = parseInt(process.env.VUE_APP_START_DAY, 10);
    let startDatetime;
    let endDatetime;

    if (!useTimeFilter) {
      startDatetime = moment(startDate).hours(siteOpeningHours);
      endDatetime = moment(endDate)
        .add(isSiteOpeningHours ? 1 : 0, 'days')
        .hours(siteOpeningHours);
    } else {
      const parsedStartTime = parseInt(startTime, 10);
      const parsedEndTime = parseInt(endTime, 10);
      startDatetime = parsedStartTime >= siteOpeningHours
        ? moment(startDate).hours(parsedStartTime)
        : moment(startDate).add(1, 'days').hours(parsedStartTime);
      endDatetime = parsedEndTime > siteOpeningHours
        ? moment(endDate).hours(parsedEndTime)
        : moment(endDate).add(1, 'days').hours(parsedEndTime);
    }

    let label = prefix;
    const start = dateFormat(startDatetime, "datePickerPeriodMedium");
    const end = dateFormat(endDatetime, "datePickerPeriodMedium");
    const isEndToday = moment(endDate).isSame(moment(), 'day');

    label = isEndToday
      ? label + Tools.StringHelper.format(i18n.t("filter.period_now_range"), start)
      : label + Tools.StringHelper.format(i18n.t("filter.period_date_range"), start, end);

    label =
      label + Tools.StringHelper.format(i18n.t("filter.period_time_range"), startTime, endTime);

    return label
  };
  _self.getLabelPeriodOneDay = function (startDate, labelPrefix, useTimeFilter, startTime, endTime) {
    const isToday = _self.getTodaysBusinessDate().isSame(startDate, 'day');
    let
      label = labelPrefix || "",
      start,
      end;

    if (!useTimeFilter) { // Permet de gérer le label des datepicker sans filtre horaire
      const momentStartDate = moment(startDate).hours(process.env.VUE_APP_START_DAY);
      const momentEndDate = moment(startDate).add(1, 'days').hours(process.env.VUE_APP_START_DAY);
      start = dateFormat(momentStartDate, "datePickerPeriod");
      end = dateFormat(momentEndDate, "datePickerPeriod");
    } else {
      const momentStartDateTime = parseInt(startTime) >= parseInt(process.env.VUE_APP_START_DAY)
        ? moment(startDate).hours(startTime)
        : moment(startDate).add(1, 'days').hours(startTime);
      const momentEndDateTime = parseInt(endTime) > parseInt(process.env.VUE_APP_START_DAY)
        ? moment(startDate).hours(endTime)
        : moment(startDate).add(1, 'days').hours(endTime);
      start = dateFormat(momentStartDateTime, "datePickerPeriod");
      end = dateFormat(momentEndDateTime, "datePickerPeriod");
    }

    label = isToday
      ? label + Tools.StringHelper.format(i18n.t("filter.period_now"), start)
      : label + Tools.StringHelper.format(i18n.t("filter.period_full"), start, end);

    return label;
  };
  _self.getOrderFromBusinessHour = function (hour) {
    const offset = parseInt(process.env.VUE_APP_START_DAY);
    return Tools.ConvertHelper.modulo((hour - offset), 24);
  };
  _self.getBusinessHourFromOrder = function (hourOrder) {
    const offset = parseInt(process.env.VUE_APP_START_DAY);
    return Tools.ConvertHelper.modulo((hourOrder + offset), 24);
  };
};

/** String Helper */
var stringHelperManager = function () {
  var _self = this;
  _self.format = function (pattern, ...args) {
    if (typeof pattern === "undefined" || pattern === null || pattern === "")
      throw "[format] error pattern cannot be null or empty";
    if (args.length > 0) {
      let result = pattern;
      for (let i = 0; i < args.length; i++) {
        let regPattern = "\\{" + i + "\\}";
        let regex = new RegExp(regPattern, "g");
        result = result.replace(regex, args[i]);
      }
      return result;
    }
    return pattern;
  };

  _self.cleanSpacesAndSpecialCharacters = function (str) {
    // Mapping des accents vers leurs équivalents sans accents
    const accentMap = {
      'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a',
      'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
      'ù': 'u',
      'ç': 'c', 'ñ': 'n',
      // Versions majuscules
      'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A', 'Å': 'A',
      'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
      'Ù': 'U',
      'Ç': 'C', 'Ñ': 'N'
    };

    // Remplacer les accents
    let cleanStr = str.replace(/[àáâãäåèéêëùçñÀÁÂÃÄÅÈÉÊËÙÇÑ]/g,
      match => accentMap[match] || match
    );

    // Ensuite nettoyer les caractères spéciaux (garde maintenant A-Z et a-z)
    return cleanStr.replace(/[^A-Za-z0-9]+/gi, "-");
  };

  _self.constructExportFilename = function (filenamePrefix, labelSite, dateRangeStr) {
    let filename = this.cleanSpacesAndSpecialCharacters(filenamePrefix);
    if (labelSite)
      filename += "_" + this.cleanSpacesAndSpecialCharacters(labelSite);
    if (dateRangeStr)
      filename += "_" + this.cleanSpacesAndSpecialCharacters(dateRangeStr);

    return filename;
  };

  _self.constructExportSubtitles = function (labelsSites, labelDatePeriod, labelTimeRange, labelDatePeriod2) {
    const labelSites = labelsSites && Array.isArray(labelsSites)
      ? labelsSites.join(", ")
      : "";

    let line2 = labelDatePeriod;
    if (labelTimeRange)
      line2 += ", " + labelTimeRange;

    let subtitles = [labelSites, line2];

    let line3 = labelDatePeriod2;
    if (labelDatePeriod2 && labelTimeRange)
      line3 += ", " + labelTimeRange;

    if (line3)
      subtitles.push(line3);

    return subtitles;
  }
};

/** Convert Helper */
var convertHelperManager = function () {
  var _self = this;
  _self.format = function pixelToMM(value) {
    const onePixelInMm = 0.2645833333;
    return value * onePixelInMm;
  };

  /** Fonction modulo qui fonctionne avec les nombres négatifs */
  _self.modulo = function (n, m) {
    return ((n % m) + m) % m;
  }
};

/** Helper divers */
var helperManager = function () {
  var _self = this;

  _self.getFlatPickerConfig = function (dateFormat, rangeMode, allowFutureDate, useMonthPlugin = false) {
    var todaysBusinessDate = Tools.BusinessDateHelper.getTodaysBusinessDate();
    var config = {
      dateFormat: convertToFlatpickrFormat(dateFormat),
      enableTime: false,
      allowInput: false,
      closeOnSelect: false,
      maxDate: allowFutureDate ?
        todaysBusinessDate.add(1, 'Y').format(dateFormat).toString() :
        todaysBusinessDate.format(dateFormat).toString(),
      plugins: [],
    };
    if (rangeMode) config.mode = "range";
    if (useMonthPlugin) {
      config.plugins.push(
        new monthSelectPlugin({
          dateFormat: convertToFlatpickrFormat(dateFormat),
        })
      );
    }
    return config;
  };
};

/** AlertManager avec swal */
var alertManager = function () {
  var _self = this;
  _self.Message = function (swalInstance, message, title) {
    return _self.ShowMessage(swalInstance, message, "info", title);
  };
  _self.Alert = function (swalInstance, message, title) {
    if (message) {
      console.warn(message);
    }
    return _self.ShowMessage(swalInstance, message, "warning", title);
  };

  _self.Success = function (swalInstance, message, title) {
    return _self.ShowMessage(swalInstance, message, "success", title);
  };

  _self.Error = function (swalInstance, error, title) {
    if (error) {
      console.error(error);
    }
    if (error === undefined || error === null) {
      message = i18n.t("alerts.unexpectedError");
      return _self.ShowMessage(swalInstance, message, "error");
    } else if (typeof error === "string") {
      return _self.ShowMessage(swalInstance, error, "error", title);
    } else if (error.status === 404) {
      return _self.ShowMessage(
        swalInstance,
        i18n.t("alerts.notFound"),
        "error"
      );
      // } else if (error.status === 401) {
      //     InweTools.Ajax.ErrorHandler(error); // TODO besoin d'un error handler ??
    } else {
      var message;
      if (error.status === 400) {
        if (error.body && error.body.Message) {
          message = error.body.Message;
        } else {
          message = error.statusText;
        }
      } else {
        message = i18n.t("alerts.unexpectedError");
      }
      return _self.ShowMessage(swalInstance, message, "error");
    }
  };

  _self.ShowMessage = function (swalInstance, message, type, title) {
    return swalInstance.fire({
      position: "center",
      icon: type || "info",
      title: title || "",
      html: message,
      showConfirmButton: true,
      confirmButtonText: i18n.t("alerts.alertConfirmButton"),
    });
  };
};

var storageHelperManager = function () {
  var _self = this;
  // eslint-disable-next-line
  const pivotDateFormat = dateFormatEnum.MOMENT_DATE_FORMAT;

  _self.setStorageItem = function (storage, key, data) {
    if (data == null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, data);
    }
  };

  _self.storeFilterSelectedValue = function (key, value) {
    _self.setStorageItem(sessionStorage, "filter." + key, value ? JSON.stringify(value) : null);
  };

  _self.getStoredFilterSelectedValue = function (key) {
    const storedValue = sessionStorage.getItem("filter." + key);
    return storedValue ? JSON.parse(storedValue) : null;
  };

  /**
   * Stocke un tableau de dates en sessionStorage, en les transformant en un format pivot
   * @param {String} key : identifiant pour récupérer ensuite les données stockées
   * @param {Array of String} arrayOfDates : tableau de dates en string, au format indiqué
   * @param {String} inputDatesFormat :format des dates fournies, pour qu'elles puissent être stockées dans un format pivot
   */
  _self.storeDateRangeSelectedValues = function (key, arrayOfDates, inputDatesFormat) {
    if (arrayOfDates && arrayOfDates.length && inputDatesFormat) {
      const dateRange = arrayOfDates.map((dateStr) =>
        moment(dateStr, inputDatesFormat).format(_self.pivotDateFormat).toString()
      );
      _self.setStorageItem(sessionStorage, "daterange." + key, JSON.stringify(dateRange));
    } else {
      _self.setStorageItem(sessionStorage, "daterange." + key, null);
    }
  };

  /**
   * Récupère un tableau de dates stockées en session storage, en les transformant au format demandé
   * @param {String} key : identifiant pour récupérer ensuite les données stockées
   * @param {String} expectedDatesFormat : format des dates attendues
   * @returns {Array of String} : tableau de dates en string, au format demandé
   */
  _self.getStoredDateRangeSelectedValues = function (key, expectedDatesFormat) {
    const storedValue = sessionStorage.getItem("daterange." + key);
    if (storedValue) {
      const dateRange = JSON.parse(storedValue);
      return dateRange.map((dateStr) =>
        moment(dateStr, _self.pivotDateFormat).format(expectedDatesFormat).toString()
      );
    } else {
      return null;
    }
  };
};

/** Instanciation of all helpers */
Tools.StringHelper = new stringHelperManager();
Tools.BusinessDateHelper = new businessDateHelperManager();
Tools.ConvertHelper = new convertHelperManager();
Tools.Helper = new helperManager();
Tools.Alerts = new alertManager();
Tools.StorageHelper = new storageHelperManager();
